import React, { useEffect, useState } from "react";
import { Sparkles, Star, Music } from "lucide-react";

/**
 * Quest grade popup with grade-specific animation.
 * Grade 1 (F): violin + sad faces
 * Grade 2 (D): cat falls down
 * Grade 3 (B/C): some sparkles
 * Grade 4 (A): glowing stars + confetti + dance + hat
 */
export const QuestGradePopup = ({ notif, onClose }) => {
    const meta = notif?.meta || {};
    const grade = meta.grade || 3;
    const [stage, setStage] = useState("anim"); // 'anim' | 'card'

    useEffect(() => {
        const t = setTimeout(() => setStage("card"), 2200);
        return () => clearTimeout(t);
    }, []);

    if (!notif) return null;

    const gradeMeta = {
        4: { letter: "A", label: "Outstanding!", color: "#ffd166", border: "#ffd166" },
        3: { letter: "B", label: "Great work!", color: "#7cc1ff", border: "#7cc1ff" },
        2: { letter: "C", label: "Stumbled", color: "#ff9f1c", border: "#ff9f1c" },
        1: { letter: "F", label: "Tough one", color: "#ff6b6b", border: "#ff6b6b" },
    }[grade];

    return (
        <div
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4"
            data-testid="quest-grade-popup"
        >
            {/* Background effects */}
            {grade >= 3 && <ConfettiBurst />}
            {grade === 4 && <GlowingStars />}
            {grade <= 2 && <SadFloaters violin={grade === 1} />}

            <div
                className="relative bb-glass rounded-3xl p-8 max-w-md w-full text-center"
                style={{ borderColor: gradeMeta.border, borderWidth: 2 }}
            >
                <p className="text-xs uppercase tracking-[0.25em] text-[#6c7a9c] mb-2">
                    Quest graded
                </p>
                <h2 className="font-display text-3xl mb-1">{meta.quest_name}</h2>
                <p className="text-sm text-[#b0b8d6] mb-6">{meta.quest_subject}</p>

                <div className="my-6">
                    <CatReaction grade={grade} stage={stage} />
                </div>

                <div className="flex items-center justify-center gap-4 mb-3">
                    <div
                        className="text-7xl font-display font-black leading-none"
                        style={{ color: gradeMeta.color, textShadow: `0 0 30px ${gradeMeta.color}66` }}
                        data-testid="grade-letter"
                    >
                        {gradeMeta.letter}
                    </div>
                    <div className="text-left">
                        <p className="text-sm font-semibold" style={{ color: gradeMeta.color }}>
                            {gradeMeta.label}
                        </p>
                        <p className="text-xs text-[#b0b8d6]">{grade}/4</p>
                        <p className="text-xs text-[#ffd166] mt-1">
                            +{meta.awarded_points || 0} pts earned
                        </p>
                    </div>
                </div>

                {meta.message && (
                    <div className="mt-4 p-3 rounded-xl bg-white/5 border border-white/10 text-sm text-[#b0b8d6] italic">
                        "{meta.message}"
                    </div>
                )}

                <button
                    onClick={onClose}
                    className="bb-btn-primary mt-6"
                    data-testid="grade-popup-close"
                >
                    {grade >= 3 ? "Yay!" : grade === 2 ? "Try again next time" : "I'll keep trying"}
                </button>
            </div>
        </div>
    );
};

const CatReaction = ({ grade, stage }) => {
    if (grade === 4) {
        return (
            <div className="relative inline-block">
                <span
                    className="absolute -top-6 left-1/2 -translate-x-1/2 text-3xl"
                    style={{ animation: "bb-float-bouncy 1.2s ease-in-out infinite" }}
                >
                    🎩
                </span>
                <span
                    className="text-7xl inline-block bb-celebrate-dance"
                    style={{ animationIterationCount: "infinite", animationDuration: "0.8s" }}
                >
                    😺
                </span>
            </div>
        );
    }
    if (grade === 3) {
        return (
            <span
                className="text-7xl inline-block bb-celebrate-bounce"
                style={{ animationIterationCount: "infinite", animationDuration: "1.2s" }}
            >
                😸
            </span>
        );
    }
    if (grade === 2) {
        return (
            <span
                className="text-7xl inline-block"
                style={{
                    display: "inline-block",
                    animation: stage === "anim" ? "bb-fall 1.2s ease-in 1 forwards" : "none",
                }}
            >
                🙀
            </span>
        );
    }
    // grade === 1
    return (
        <div className="relative inline-block">
            <span className="text-7xl inline-block">😿</span>
            <span
                className="absolute -bottom-2 -right-4 text-4xl"
                style={{ animation: "bb-float-droop 2s ease-in-out infinite" }}
            >
                🎻
            </span>
            <Music
                className="absolute -top-6 -left-2 text-[#7cc1ff]"
                size={20}
                style={{ animation: "bb-float-bouncy 1.6s ease-in-out infinite" }}
            />
            <Music
                className="absolute -top-2 -right-8 text-[#b388ff]"
                size={16}
                style={{ animation: "bb-float-bouncy 1.4s ease-in-out infinite", animationDelay: "0.4s" }}
            />
        </div>
    );
};

const ConfettiBurst = () => {
    const pieces = Array.from({ length: 36 });
    return (
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {pieces.map((_, i) => {
                const left = Math.random() * 100;
                const dur = 1.6 + Math.random() * 1.6;
                const delay = Math.random() * 0.4;
                const color = ["#ffd166", "#7cc1ff", "#b388ff", "#ff6fa1", "#4ade80"][i % 5];
                return (
                    <span
                        key={i}
                        className="absolute"
                        style={{
                            top: "-10%",
                            left: `${left}%`,
                            width: 8,
                            height: 14,
                            background: color,
                            transform: `rotate(${Math.random() * 360}deg)`,
                            animation: `bb-star-fall ${dur}s ${delay}s linear forwards`,
                            borderRadius: 2,
                        }}
                    />
                );
            })}
        </div>
    );
};

const GlowingStars = () => {
    const stars = Array.from({ length: 14 });
    return (
        <div className="absolute inset-0 pointer-events-none">
            {stars.map((_, i) => {
                const top = 10 + Math.random() * 80;
                const left = Math.random() * 100;
                const dur = 2 + Math.random() * 2;
                const delay = Math.random() * 1.2;
                return (
                    <Star
                        key={i}
                        size={20 + Math.random() * 20}
                        fill="#ffd166"
                        className="absolute text-[#ffd166]"
                        style={{
                            top: `${top}%`,
                            left: `${left}%`,
                            filter: "drop-shadow(0 0 16px rgba(255,209,102,0.9))",
                            animation: `bb-twinkle ${dur}s ${delay}s ease-in-out infinite`,
                        }}
                    />
                );
            })}
        </div>
    );
};

const SadFloaters = ({ violin }) => {
    const items = Array.from({ length: 8 });
    return (
        <div className="absolute inset-0 pointer-events-none">
            {items.map((_, i) => {
                const top = 10 + Math.random() * 80;
                const left = Math.random() * 100;
                const dur = 4 + Math.random() * 3;
                return (
                    <span
                        key={i}
                        className="absolute opacity-70"
                        style={{
                            top: `${top}%`,
                            left: `${left}%`,
                            fontSize: 22 + Math.random() * 10,
                            animation: `bb-float-droop ${dur}s ease-in-out infinite`,
                        }}
                    >
                        {violin && i % 3 === 0 ? "🎵" : "💧"}
                    </span>
                );
            })}
        </div>
    );
};

export default QuestGradePopup;
